import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { fmtLocalDate } from '../../../utils/dateUtils';

// Sunday first, same as the date picker the dates were chosen in.
const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

// Only dates an edit adds or removes get a colour; scheduled days are dark, the rest of the month is faint.
const MARK = {
  added:   { color: '#15803d', fontWeight: 700, textDecoration: 'none' },
  removed: { color: '#b91c1c', fontWeight: 600, textDecoration: 'line-through' },
};

const GRID_LINE = '1px solid rgba(100, 116, 139, 0.2)';

const monthLabel = (key) => {
  try {
    return new Date(`${key}-01T00:00:00`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  } catch {
    return key;
  }
};

function MonthCalendar({ monthKey, scheduled, marked, markTone }) {
  const [y, m] = monthKey.split('-').map(Number);
  const firstWeekday = new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const count = scheduled.size;
  const trailingBlanks = (7 - ((firstWeekday + daysInMonth) % 7)) % 7;   // finish the last week so the grid closes

  return (
    <Box sx={{ flex: '1 1 200px', maxWidth: 260, minWidth: 0 }}>
      <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: '#475569', mb: 0.5 }}>
        {monthLabel(monthKey)} · {count} day{count === 1 ? '' : 's'}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          borderTop: GRID_LINE,
          borderLeft: GRID_LINE,
          '& > *': { borderRight: GRID_LINE, borderBottom: GRID_LINE },
        }}
      >
        {WEEKDAYS.map((w, i) => (
          <Typography
            key={`h${i}`}
            component="span"
            sx={{ fontSize: 10, fontWeight: 600, color: '#64748b', textAlign: 'center', lineHeight: '20px', bgcolor: 'rgba(100, 116, 139, 0.08)' }}
          >
            {w}
          </Typography>
        ))}

        {Array.from({ length: firstWeekday }, (_, i) => <Box key={`b${i}`} />)}

        {Array.from({ length: daysInMonth }, (_, i) => {
          const day = i + 1;
          const iso = `${monthKey}-${String(day).padStart(2, '0')}`;
          const isScheduled = scheduled.has(iso);
          const mark = isScheduled && marked?.has(iso) ? MARK[markTone] : null;
          return (
            <Typography
              key={iso}
              component="span"
              sx={{
                fontSize: 12, lineHeight: '22px', textAlign: 'center', fontVariantNumeric: 'tabular-nums',
                color: isScheduled ? '#1e293b' : '#d5dde8',
                fontWeight: isScheduled ? 600 : 400,
                textDecoration: 'none',
                ...mark,
              }}
            >
              {day}
            </Typography>
          );
        })}

        {Array.from({ length: trailingBlanks }, (_, i) => <Box key={`t${i}`} />)}
      </Box>
    </Box>
  );
}

/**
 * Run dates as a short summary (day count + first–last date) followed by one small calendar per month, with the scheduled
 * days in dark text — so gaps and the weekday of each date are visible at a glance. No badges.
 *
 * dates     ["yyyy-MM-dd", ...] in any order
 * marked    optional Set of dates to highlight (e.g. the ones an edit adds or removes)
 * markTone  'added' (green, bold) | 'removed' (red, struck through)
 */
export default function RunDatesGrid({ dates, marked, markTone = 'added' }) {
  const { sorted, months } = useMemo(() => {
    const list = [...new Set(Array.isArray(dates) ? dates : [])].sort();
    const byMonth = new Map();
    list.forEach((d) => {
      const key = String(d).slice(0, 7);
      if (!byMonth.has(key)) byMonth.set(key, new Set());
      byMonth.get(key).add(d);
    });
    return { sorted: list, months: [...byMonth.entries()] };
  }, [dates]);

  if (sorted.length === 0) {
    return <Typography sx={{ fontSize: 12.5, color: '#374151' }}>—</Typography>;
  }

  return (
    <Box>
      <Typography sx={{ fontSize: 12.5, color: '#374151', lineHeight: 1.5, mb: 0.75 }}>
        <Box component="span" sx={{ fontWeight: 700 }}>{sorted.length} day{sorted.length === 1 ? '' : 's'}</Box>
        {'  ·  '}
        {sorted.length === 1
          ? fmtLocalDate(sorted[0])
          : `${fmtLocalDate(sorted[0])} – ${fmtLocalDate(sorted[sorted.length - 1])}`}
      </Typography>

      {/* As many months per row as fit (each 200-260px wide); a single month shrinks to a narrow cell */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2.5, rowGap: 1.5 }}>
        {months.map(([key, scheduled]) => (
          <MonthCalendar key={key} monthKey={key} scheduled={scheduled} marked={marked} markTone={markTone} />
        ))}
      </Box>
    </Box>
  );
}
